/**
 * useTooltip.ts - Hook for managing tooltip state
 * 
 * CHANGE HISTORY:
 * - 251105_1915 danielbeer: Added support for keeping tooltip visible when hovering over it
 * - 251101_0756 danielbeer: Initial creation
 */

import { useState, useCallback, useRef } from 'react';
import type React from 'react';

export interface TooltipState {
  isVisible: boolean;
  position: { x: number; y: number };
  content: React.ReactNode | string;
  title?: string;
  keepOnHover?: boolean;  // New: Keep tooltip visible when hovering over it
}

export const useTooltip = () => {
  const [tooltip, setTooltip] = useState<TooltipState>({
    isVisible: false,
    position: { x: 0, y: 0 },
    content: '',
    title: undefined,
    keepOnHover: true,  // Default: keep tooltip visible on hover
  });
  
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showTooltip = useCallback((
    event: React.MouseEvent | MouseEvent,
    content: React.ReactNode | string,
    title?: string,
    keepOnHover: boolean = true
  ) => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    
    setTooltip({
      isVisible: true,
      position: {
        x: event.clientX,
        y: event.clientY,
      },
      content,
      title,
      keepOnHover,
    });
  }, []);

  const hideTooltip = useCallback((immediate: boolean = false) => {
    if (immediate || !tooltip.keepOnHover) {
      // Clear any pending timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      
      setTooltip(prev => ({
        ...prev,
        isVisible: false,
      }));
    } else {
      // Delay hiding to allow user to move mouse to tooltip
      hideTimeoutRef.current = setTimeout(() => {
        setTooltip(prev => ({
          ...prev,
          isVisible: false,
        }));
      }, 200);  // 200ms delay
    }
  }, [tooltip.keepOnHover]);
  
  const cancelHide = useCallback(() => {
    // Cancel any pending hide
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const updatePosition = useCallback((event: React.MouseEvent | MouseEvent) => {
    setTooltip(prev => ({
      ...prev,
      position: {
        x: event.clientX,
        y: event.clientY,
      },
    }));
  }, []);

  return {
    tooltip,
    showTooltip,
    hideTooltip,
    cancelHide,
    updatePosition,
  };
};
